import type { EntityStatus } from '@ms/yammer-data-support';
import type {
  AutocompleteGroupResultFilter,
  AutocompleteOfficeGroupResultFilter,
  AutocompleteTopicResultFilter,
  AutocompleteUserResultFilterType,
} from '@ms/yammer-graphql';
import createCachedSelector from 're-reselect';

import type {
  CampaignReference,
  Group,
  OfficeGroup,
  Publisher,
  Topic,
  TypeaheadRequests,
  TypeaheadResult,
  TypeaheadResults as TypeaheadResultMatchedTerms,
  Typeaheadable,
  User,
  YammerState,
} from '../../../state/types';
import { getFulfilledCampaignReferences } from '../../campaignReferences/selectors';
import { getFulfilledGroups } from '../../groups/selectors';
import { getFulfilledOfficeGroups } from '../../officeGroups/selectors';
import { getFulfilledTopics } from '../../topics/selectors';
import { getFulfilledUsers } from '../../users/selectors';
import createDeepEqualSelector from '../createDeepEqualSelector';
import { getId as getTypeaheadId } from '../getId';

export const maxUserResultsCount = 8;
export const maxCampaignResultsCount = 8;
export const maxGroupResultsCount = 20;
export const maxTopicResultsCount = 20;
export const maxOfficeGroupResultsCount = 20;
export const vivaTopicCount = 50;

export interface TypeaheadStatuses {
  readonly users?: EntityStatus;
  readonly groups?: EntityStatus;
  readonly topics?: EntityStatus;
  readonly officeGroups?: EntityStatus;
  readonly campaignReferences?: EntityStatus;
}

type IsFulfilled = (state: YammerState, parameters: TypeaheadSearchParameters, type: keyof TypeaheadResults) => boolean;
export const isFulfilled: IsFulfilled = (state, parameters, type) =>
  getTypeaheadStatuses(state, parameters)[type] === 'fulfilled';

type IsRejected = (state: YammerState, parameters: TypeaheadSearchParameters, type: keyof TypeaheadResults) => boolean;
export const isRejected: IsRejected = (state, parameters, type) =>
  getTypeaheadStatuses(state, parameters)[type] === 'rejected';

type GetTypeaheadRequests = (state: YammerState) => TypeaheadRequests;
export const getTypeaheadRequests: GetTypeaheadRequests = (state: YammerState) => state.typeahead.requests;

type GetTypeaheadResultMatchedTerms = (state: YammerState) => TypeaheadResultMatchedTerms;
export const getTypeaheadResultMatchedTerms: GetTypeaheadResultMatchedTerms = (state) => state.typeahead.results;

type GetTypeaheadStatuses = (state: YammerState, parameters: TypeaheadSearchParameters) => TypeaheadStatuses;
export const getTypeaheadStatuses: GetTypeaheadStatuses = createCachedSelector(
  getTypeaheadRequests,
  (_state: YammerState, parameters: TypeaheadSearchParameters) => parameters,
  (
    requests,
    {
      search,
      groupIds,
      userResultFilters,
      groupResultFilter,
      topicResultFilter,
      officeGroupResultFilter,
      onBehalfOfUserId,
      contextGroupId,
    }
  ) => ({
    users:
      requests.users[
        getTypeaheadId({
          search,
          type: 'users',
          groupIds,
          userResultFilters,
        })
      ],
    groups: requests.groups[getTypeaheadId({ search, type: 'groups', groupResultFilter, onBehalfOfUserId })],
    topics: requests.topics[getTypeaheadId({ search, type: 'topics', topicResultFilter })],
    officeGroups: requests.officeGroups[getTypeaheadId({ search, type: 'officeGroups', officeGroupResultFilter })],
    campaignReferences:
      requests.campaignReferences[getTypeaheadId({ search, type: 'campaignReferences', contextGroupId })],
  })
)(
  (_state, { search, groupResultFilter, groupIds, onBehalfOfUserId }) =>
    `${search.toLowerCase()}-groupResultFilter:${groupResultFilter}-groupIds:${(groupIds ?? []).join(',')}-senderId-${onBehalfOfUserId}`
);

type DoesQueryMatchSelectors = (query: string, terms: string[]) => boolean;
export const doesQueryMatchTerms: DoesQueryMatchSelectors = (query, terms) =>
  terms.some((term) => term === query.toLowerCase());

type DoesGroupIdsMatchSelectors = (queryGroupIds?: Group['id'][], cachedResultGroupIds?: Group['id'][]) => boolean;
export const doesGroupIdsMatchSelectors: DoesGroupIdsMatchSelectors = (queryGroupIds, cachedResultGroupIds) => {
  if (!queryGroupIds && !cachedResultGroupIds) {
    return true;
  }

  if (!queryGroupIds || !cachedResultGroupIds) {
    return false;
  }

  if (queryGroupIds.length !== cachedResultGroupIds.length) {
    return false;
  }

  return queryGroupIds.every((value) => cachedResultGroupIds.includes(value));
};

type DoesGroupsResultFilterMatch = (
  queryGroupResultFilter?: AutocompleteGroupResultFilter,
  cachedGroupResultFilter?: AutocompleteGroupResultFilter
) => boolean;
export const doesGroupsResultFilterMatch: DoesGroupsResultFilterMatch = (
  queryGroupResultFilter,
  cachedGroupResultFilter
) => !queryGroupResultFilter || queryGroupResultFilter === cachedGroupResultFilter;

type DoesOfficeGroupsResultFiltersMatch = (
  queryOfficeGroupResultFilter?: AutocompleteOfficeGroupResultFilter,
  cachedOfficeGroupResultFilters?: AutocompleteOfficeGroupResultFilter[]
) => boolean;
export const doesOfficeGroupsResultFiltersMatch: DoesOfficeGroupsResultFiltersMatch = (
  queryOfficeGroupResultFilter = 'EXCLUDE_M365_GROUPS',
  cachedOfficeGroupResultFilters = ['EXCLUDE_M365_GROUPS']
) => cachedOfficeGroupResultFilters.some((value) => value === queryOfficeGroupResultFilter);

const doesPassUserResultFilters = (
  requestFilters: AutocompleteUserResultFilterType[] = [],
  resultFilters: AutocompleteUserResultFilterType[] = []
) => JSON.stringify([...requestFilters].sort()) === JSON.stringify([...resultFilters].sort());

type DoesPassLeaderResultFilter = (
  isLeader: boolean | undefined,
  userResultFilters: AutocompleteUserResultFilterType[] | undefined
) => boolean;
export const doesPassLeaderResultFilter: DoesPassLeaderResultFilter = (isLeader, userResultFilters) => {
  if (!userResultFilters || userResultFilters.length === 0) {
    return true;
  }

  if (userResultFilters.includes('EXCLUDE_LEADERS')) {
    return !isLeader;
  }

  if (userResultFilters.includes('ONLY_LEADERS')) {
    return !!isLeader;
  }

  return true;
};

export interface TypeaheadSearchParameters {
  readonly search: string;
  readonly userResultFilters?: AutocompleteUserResultFilterType[];
  readonly groupResultFilter?: AutocompleteGroupResultFilter;
  readonly topicResultFilter?: AutocompleteTopicResultFilter;
  readonly groupIds?: Group['id'][];
  readonly officeGroupResultFilter?: AutocompleteOfficeGroupResultFilter;
  readonly contextGroupId?: Group['id'];
  readonly onBehalfOfUserId?: User['id'];
}

export interface GetTypeAheadResultsOptions extends TypeaheadSearchParameters {
  readonly includeUsers?: boolean;
  readonly includeGroups?: boolean;
  readonly includeTopics?: boolean;
  readonly includeOfficeGroups?: boolean;
  readonly includeCampaigns?: boolean;
}

export type TypeaheadResults = {
  [K in keyof TypeaheadResultMatchedTerms]: TypeaheadResultMatchedTerms[K] extends infer T
    ? T extends TypeaheadResult<infer U>[]
      ? U[]
      : never
    : never;
};

type Match<T extends Typeaheadable> = (results: T['id'][], result: TypeaheadResult<T>) => T['id'][];

const appendMatch = <T extends Typeaheadable>(
  results: T['id'][],
  result: TypeaheadResult<T>,
  passResultFilter: boolean
) => (passResultFilter ? [...results, result.id] : results);

type GetTypeaheadResultEntityIds = (
  state: YammerState,
  selectorOptions: GetTypeAheadResultsOptions
) => {
  readonly users: User['id'][];
  readonly groups: Group['id'][];
  readonly topics: Topic['id'][];
  readonly officeGroups: OfficeGroup['id'][];
  readonly campaignReferences: CampaignReference['id'][];
};

interface GetCacheKeyOptions {
  readonly search: string;
  readonly includeUsers?: boolean;
  readonly includeGroups?: boolean;
  readonly includeTopics?: boolean;
  readonly includeOfficeGroups?: boolean;
  readonly includeCampaigns?: boolean;
  readonly groupIds?: Group['id'][];
  readonly userResultFilters?: AutocompleteUserResultFilterType[];
  readonly groupResultFilter?: AutocompleteGroupResultFilter;
  readonly topicResultFilter?: AutocompleteTopicResultFilter;
  readonly officeGroupResultFilter?: AutocompleteOfficeGroupResultFilter;
  readonly publisherId?: Publisher['id'];
  readonly contextGroupId?: Group['id'];
  readonly onBehalfOfUserId?: User['id'];
}
type GetCacheKey = (options: GetCacheKeyOptions) => string;

export const getCacheKey: GetCacheKey = ({
  search,
  includeUsers,
  includeGroups,
  includeTopics,
  includeOfficeGroups,
  includeCampaigns,
  groupIds,
  userResultFilters,
  groupResultFilter,
  topicResultFilter,
  officeGroupResultFilter,
  publisherId,
  contextGroupId,
  onBehalfOfUserId,
}) =>
  `${search}-users:${includeUsers}-groups:${includeGroups}-topics:${includeTopics}-officeGroups:${includeOfficeGroups}
  }-groupIds:${(groupIds ?? []).join(',')}-campaignReferences:${includeCampaigns}-contextGroupId:${contextGroupId}-userResultFilters:${(userResultFilters ?? []).join(',')}-groupResultFilter:${groupResultFilter}-topicResultFilter:${topicResultFilter}-officeGroupResultFilter:${
    officeGroupResultFilter ?? 'EXCLUDE_M365_GROUPS'
  }-publisherId:${publisherId}-senderId:${onBehalfOfUserId}}`;

const getTypeaheadResultEntityIds: GetTypeaheadResultEntityIds = createCachedSelector(
  getTypeaheadResultMatchedTerms,
  (_state: YammerState, selectorOptions: GetTypeAheadResultsOptions) => selectorOptions,
  (
    stateResults,
    {
      search,
      groupResultFilter,
      includeUsers,
      includeGroups,
      includeTopics,
      includeOfficeGroups,
      includeCampaigns,
      groupIds,
      officeGroupResultFilter,
      userResultFilters,
      contextGroupId,
    }
  ) => {
    const query = search.toLowerCase();

    const matchUser: Match<User> = (results, result) => {
      const doesMatchTerms = doesQueryMatchTerms(query, result.terms);
      const areGroupIdsSame = doesGroupIdsMatchSelectors(groupIds, result.groupIds);
      const isPassedExternalUsersFilter = doesPassUserResultFilters(userResultFilters, result.userResultFilters);
      const isPassedLeaderResultFilter = doesPassLeaderResultFilter(result.isLeader, userResultFilters);
      const passedAllUserFilters =
        doesMatchTerms && areGroupIdsSame && isPassedExternalUsersFilter && isPassedLeaderResultFilter;

      return appendMatch(results, result, passedAllUserFilters);
    };

    const mactchCampaignReference: Match<CampaignReference> = (results, result) => {
      const doesMatchTerms = doesQueryMatchTerms(query, result.terms);
      const areContextGroupIdsSame = doesGroupIdsMatchSelectors(
        contextGroupId ? [contextGroupId] : [],
        result.contextGroupId ? [result.contextGroupId] : []
      );
      const passedAllCampaignReferenceFilters = doesMatchTerms && areContextGroupIdsSame;

      return appendMatch(results, result, passedAllCampaignReferenceFilters);
    };

    const matchGroup: Match<Group> = (results, result) => {
      const doesMatchTerms = doesQueryMatchTerms(query, result.terms);
      const passedGroupResultFilter = doesGroupsResultFilterMatch(groupResultFilter, result.groupResultFilter);
      const passedAllGroupFilters = doesMatchTerms && passedGroupResultFilter;

      return appendMatch(results, result, passedAllGroupFilters);
    };
    const matchSearchQuery: Match<Topic | CampaignReference> = (results, result) => {
      const doesMatchTerms = doesQueryMatchTerms(query, result.terms);

      return appendMatch(results, result, doesMatchTerms);
    };
    const matchOfficeGroup: Match<OfficeGroup> = (results, result) => {
      const doesMatchTerms = doesQueryMatchTerms(query, result.terms);
      const doesMatchOfficeGroupResultFilters = doesOfficeGroupsResultFiltersMatch(
        officeGroupResultFilter,
        result.officeGroupResultFilters
      );

      const passedAllOfficeGroupFilters = doesMatchTerms && doesMatchOfficeGroupResultFilters;

      return appendMatch(results, result, passedAllOfficeGroupFilters);
    };

    const { users, groups, topics, officeGroups, campaignReferences } = stateResults;

    return {
      users: includeUsers ? users.reduce(matchUser, []) : [],
      groups: includeGroups ? groups.reduce(matchGroup, []) : [],
      topics: includeTopics ? topics.reduce(matchSearchQuery, []) : [],
      officeGroups: includeOfficeGroups ? officeGroups.reduce(matchOfficeGroup, []) : [],
      campaignReferences: includeCampaigns ? campaignReferences.reduce(mactchCampaignReference, []) : [],
    };
  }
)(
  (
    _state,
    {
      search,
      includeUsers,
      includeGroups,
      includeTopics,
      includeOfficeGroups,
      includeCampaigns,
      groupResultFilter,
      groupIds,
      userResultFilters,
      topicResultFilter,
      officeGroupResultFilter,
      contextGroupId,
      onBehalfOfUserId,
    }
  ) =>
    getCacheKey({
      search,
      includeUsers,
      includeGroups,
      includeTopics,
      includeOfficeGroups,
      includeCampaigns,
      groupIds,
      userResultFilters,
      groupResultFilter,
      topicResultFilter,
      officeGroupResultFilter,
      contextGroupId,
      onBehalfOfUserId,
    }),
  {
    selectorCreator: createDeepEqualSelector,
  }
);

type GetEntity<T extends Typeaheadable> = (results: T[], entityId: T['id']) => T[];

const getEntity = <T extends Typeaheadable>(results: T[], entityId: T['id'], entityBucket: Record<T['id'], T>) => {
  const entity = entityBucket[entityId];

  if (entity) {
    return [...results, entity];
  }

  return results;
};

type GetTypeaheadResults = (state: YammerState, selectorOptions: GetTypeAheadResultsOptions) => TypeaheadResults;
export const getTypeaheadResults: GetTypeaheadResults = createCachedSelector(
  getTypeaheadResultEntityIds,
  getFulfilledUsers,
  getFulfilledGroups,
  getFulfilledTopics,
  getFulfilledOfficeGroups,
  getFulfilledCampaignReferences,
  (resultIds, usersBucket, groupsBucket, topicsBucket, officeGroupBucket, campaignReferencesBucket) => {
    const getUserEntity: GetEntity<User> = (results, result) => getEntity(results, result, usersBucket);
    const getGroupEntity: GetEntity<Group> = (results, result) => getEntity(results, result, groupsBucket);
    const getTopicEntity: GetEntity<Topic> = (results, result) => getEntity(results, result, topicsBucket);
    const getOfficeGroupEntity: GetEntity<OfficeGroup> = (results, result) =>
      getEntity(results, result, officeGroupBucket);
    const getCampaignReferenceEntity: GetEntity<CampaignReference> = (results, result) =>
      getEntity(results, result, campaignReferencesBucket);

    const { users, groups, topics, officeGroups, campaignReferences } = resultIds;

    return {
      users: users.reduce(getUserEntity, []),
      groups: groups.reduce(getGroupEntity, []),
      topics: topics.reduce(getTopicEntity, []),
      officeGroups: officeGroups.reduce(getOfficeGroupEntity, []),
      campaignReferences: campaignReferences.reduce(getCampaignReferenceEntity, []),
    };
  }
)(
  (
    _state,
    {
      search,
      includeUsers,
      includeGroups,
      includeTopics,
      includeCampaigns,
      includeOfficeGroups,
      groupIds,
      userResultFilters,
      groupResultFilter,
      topicResultFilter,
      officeGroupResultFilter,
      contextGroupId,
      onBehalfOfUserId,
    }
  ) =>
    getCacheKey({
      search,
      includeUsers,
      includeGroups,
      includeTopics,
      includeOfficeGroups,
      includeCampaigns,
      groupIds,
      userResultFilters,
      groupResultFilter,
      topicResultFilter,
      officeGroupResultFilter,
      contextGroupId,
      onBehalfOfUserId,
    }),
  {
    selectorCreator: createDeepEqualSelector,
  }
);

export const extractSearchTerm = (
  _state: YammerState,
  _publisherId: Publisher['id'],
  selectorOptions: GetTypeAheadResultsOptions
): string => selectorOptions.search;

export const getRegularTypeaheadStatuses = (
  state: YammerState,
  _publisherId: Publisher['id'],
  selectorOptions: GetTypeAheadResultsOptions
): TypeaheadStatuses => getTypeaheadStatuses(state, selectorOptions);

export const getRegularTypeaheadResults = (
  state: YammerState,
  _publisherId: Publisher['id'],
  selectorOptions: GetTypeAheadResultsOptions
): TypeaheadResults => getTypeaheadResults(state, selectorOptions);
